// 模具管理 API 服务
import request from '@/api/request'
import type {
  Mold,
  MoldListQuery,
  CreateMoldRequest,
  UpdateMoldRequest,
  MoldStatistics,
  MoldPart,
  CreateMoldPartRequest,
  UpdateMoldPartRequest,
  MoldPartListQuery,
  BorrowPartsRequest,
  BorrowRecord,
  BorrowApplyRequest,
  ApprovalRequest,
  ReturnRequest,
  ReturnConfirmRequest,
  MoldTransfer,
  MoldTransferRequest,
  PageResult,
  ReturnApplicationItem,
  CommentRequest,
} from '@/types/mold'

// 获取模具列表（分页）
export async function getMoldList(params?: MoldListQuery): Promise<PageResult<Mold>> {
  const data = await request.get<Mold, PageResult<Mold>>('/molds/list', { params })
  return data
}

// 获取表头筛选的下拉候选值（各枚举列在库内实际存在的值，2026-09-22）
// 返回：前端列名(prop) -> 候选值数组，如 { warehouseLocation: ['封存库','可用库',...] }
export async function getMoldFilterOptions(): Promise<Record<string, string[]>> {
  const data = await request.get<Record<string, string[]>, Record<string, string[]>>('/molds/filter-options')
  return data
}

// 获取可再入库的模具列表
export async function getEligibleMolds(): Promise<Mold[]> {
  const data = await request.get<Mold[], Mold[]>('/molds/eligible-for-rework')
  return data
}

// 获取模具详情
export async function getMoldDetail(id: number): Promise<Mold> {
  const data = await request.get<Mold, Mold>(`/molds/${id}`)
  return data
}

// 获取模具照片（Base64格式）
export interface MoldPhotoResponse {
  frontPhoto: string | null
  backPhoto: string | null
  frontPhotoThumbnail: string | null
  backPhotoThumbnail: string | null
}

export async function getMoldPhotos(moldId: number): Promise<MoldPhotoResponse> {
  const data = await request.get<MoldPhotoResponse, MoldPhotoResponse>(`/molds/${moldId}/photos`)
  return data
}

// 搜索模具（根据模号搜索，返回列表）
export async function searchMoldCodes(keyword: string): Promise<Mold[]> {
  const data = await request.get<Mold[], { records: Mold[] }>('/molds/list', {
    params: { keyword, pageSize: 20 },
  })
  return data.records || []
}

// 根据模具编号获取模具
export async function getMoldByCode(moldCode: string): Promise<Mold> {
  const data = await request.get<Mold, Mold>(`/molds/code/${moldCode}`)
  return data
}

// 创建模具 - 后端路径是 POST /api/molds
export async function createMold(data: CreateMoldRequest): Promise<number> {
  const result = await request.post<number, number>('/molds', data)
  return result
}

// 更新模具 - 后端路径是 PUT /api/molds
export async function updateMold(data: UpdateMoldRequest): Promise<void> {
  await request.put<void, void>('/molds', data)
}

// 删除模具
export async function deleteMold(id: number): Promise<void> {
  await request.delete<void>(`/molds/${id}`)
}

// 更新模具状态
export async function updateMoldStatus(id: number, status: string): Promise<void> {
  await request.put<void, void>(`/molds/${id}/status?status=${encodeURIComponent(status)}`, {})
}

// 获取模具统计信息
export async function getMoldStatistics(): Promise<MoldStatistics> {
  const data = await request.get<MoldStatistics, MoldStatistics>('/molds/statistics')
  return data
}

// ========== 槽宽检测 ==========

export interface SlotWidthCheck {
  id?: number
  moldId?: number
  moldCode: string
  slotPosition?: string // 槽位位置
  measuredValue?: number // 检测值
  standardValue?: number // 标准值
  tolerance?: number // 公差
  isOutOfTolerance?: number // 是否超差：0-合格，1-超差
  inspector?: string // 检测人
  checkTime?: string // 检测时间
  remark?: string
  slotWidth?: number // 槽宽（从后端MoldSlotWidthCheck实体读取）
  bulkDensity?: number // 容重（从后端MoldSlotWidthCheck实体读取）
  checkUserId?: number
  checkUserName?: string
}

export interface SlotWidthCheckQuery {
  page?: number
  pageSize?: number
  keyword?: string
  moldCode?: string // 按模号筛选
}

// 获取槽宽检测列表
export async function getSlotWidthCheckList(
  params?: SlotWidthCheckQuery
): Promise<PageResult<SlotWidthCheck>> {
  const data = await request.get<SlotWidthCheck, PageResult<SlotWidthCheck>>(
    '/mold-slot-width-checks',
    { params }
  )
  return data
}

// 获取模具的槽宽检测记录（便捷方法）
export async function getSlotWidthChecksByMold(
  moldCode: string,
  page = 1,
  pageSize = 20
): Promise<{ records: SlotWidthCheck[]; total: number }> {
  const result = await getSlotWidthCheckList({ moldCode, page, pageSize })
  return { records: result.records || [], total: result.total || 0 }
}

export interface SlotWidthDensityHistory {
  checkTime: string
  slotWidth?: number
  bulkDensity?: number
  checkUserName?: string
}

// 获取模具的槽宽与容重历史数据（用于图表展示）
export async function getSlotWidthDensityHistory(
  moldId: number
): Promise<SlotWidthDensityHistory[]> {
  const data = await request.get<SlotWidthDensityHistory, SlotWidthDensityHistory[]>(
    '/mold-slot-width-checks/history',
    { params: { moldId } }
  )
  return data
}

// 获取槽宽检测详情
export async function getSlotWidthCheckDetail(id: number): Promise<SlotWidthCheck> {
  const data = await request.get<SlotWidthCheck, SlotWidthCheck>(`/mold-slot-width-checks/${id}`)
  return data
}

// 创建槽宽检测
export async function createSlotWidthCheck(data: SlotWidthCheck): Promise<number> {
  const result = await request.post<number, number>('/mold-slot-width-checks', data)
  return result
}

// 更新槽宽检测
export async function updateSlotWidthCheck(id: number, data: SlotWidthCheck): Promise<void> {
  await request.put<void, void>(`/mold-slot-width-checks/${id}`, data)
}

// 删除槽宽检测
export async function deleteSlotWidthCheck(id: number): Promise<void> {
  await request.delete<void>(`/mold-slot-width-checks/${id}`)
}

// ========= 槽宽检测任务（归还确认时建立，模管员后续执行） =========

export interface SlotWidthCheckTask {
  id: number
  moldId?: number
  moldCode?: string
  moldCategory?: string
  productSpec?: string
  standardSlotWidth?: number
  returnApplicationId?: number
  assigneeId?: number
  assigneeName?: string
  status?: string // PENDING / DONE
  isPass?: boolean
  checkRecordId?: number
  createdBy?: number
  createdByName?: string
  createTime?: string
  updateTime?: string
}

export interface SlotWidthCheckCompleteRequest {
  measuredSlotWidth: number
  /** 容重已从表单取消（用户 2026-09-14 拍板），仅兼容历史调用可传 */
  bulkDensity?: number
  deformationYPlus?: number
  deformationYMinus?: number
  deformationXPlus?: number
  deformationXMinus?: number
  isPass?: boolean
  remark?: string
}

// 模管员待执行槽宽检测任务列表
export async function getSlotWidthTasks(): Promise<SlotWidthCheckTask[]> {
  const data = await request.get<SlotWidthCheckTask, SlotWidthCheckTask[]>('/slot-width-check-tasks')
  return data
}

// 槽宽检测任务详情
export async function getSlotWidthTaskDetail(id: number): Promise<SlotWidthCheckTask> {
  const data = await request.get<SlotWidthCheckTask, SlotWidthCheckTask>(`/slot-width-check-tasks/${id}`)
  return data
}

// 执行检测：录入实测数据并完成
export async function completeSlotWidthTask(
  id: number,
  data: SlotWidthCheckCompleteRequest
): Promise<void> {
  await request.put<void, void>(`/slot-width-check-tasks/${id}/complete`, data)
}

// ========== 入库管理 ==========

export interface StorageRecord {
  id: number
  moldId: number
  moldCode: string
  storageType?: string
  productSpec?: string
  warehouseLocation?: string
  cargoLocation?: string
  qualityCheckResult?: string
  qualityCheckRemark?: string
  qualityScore?: number
  qualityCheckUserId?: number
  qualityCheckUserName?: string
  qualityCheckTime?: string
  status: string
  statusName: string
  storageUserId?: number
  storageUserName?: string
  storageTime?: string
  createUserName?: string
  createTime: string
  updateTime?: string
}

export interface StorageRequest {
  moldId?: number
  moldCode: string
  storageType: string
  productSpec?: string
  moldCategory?: string
  warehouseLocation?: string
  cargoLocation?: string
  storageUserId?: number
  storageUserName?: string
  frontPhoto?: string
  backPhoto?: string
  applicationDocuments?: string[]
  remark?: string
}

export interface QualityCheckRequest {
  qualityScore?: number
  qualityResult?: 'qualified' | 'unqualified'
  frontPhoto?: string
  backPhoto?: string
  frontPhotoFile?: File
  backPhotoFile?: File
  qualityCheckRemark?: string
  result?: 'PASS' | 'NEEDS_IMPROVEMENT' | 'FAIL'
  appearanceSizeCheck?: string
  appearanceSizeScore?: number
  appearanceSizeRemark?: string
  structureCheck?: string
  remarks?: string
}

export interface StorageRecordListQuery {
  keyword?: string
  status?: string
  storageType?: string
  moldId?: number
  page?: number
  pageSize?: number
}

// 创建入库申请
export async function createStorageRequest(data: StorageRequest): Promise<number> {
  const result = await request.post<number, number>('/molds/storage', data)
  return result
}

// 质检入库记录
export async function qualityCheckStorage(id: number, data: QualityCheckRequest): Promise<void> {
  // 使用FormData发送multipart/form-data请求
  const formData = new FormData()
  if (data.qualityScore !== undefined) formData.append('qualityScore', data.qualityScore.toString())
  if (data.qualityResult) formData.append('qualityResult', data.qualityResult)
  if (data.frontPhotoFile) formData.append('frontPhotoFile', data.frontPhotoFile)
  if (data.backPhotoFile) formData.append('backPhotoFile', data.backPhotoFile)
  if (data.qualityCheckRemark) formData.append('qualityCheckRemark', data.qualityCheckRemark)
  if (data.result) formData.append('result', data.result)
  if (data.appearanceSizeCheck) formData.append('appearanceSizeCheck', data.appearanceSizeCheck)
  if (data.appearanceSizeScore !== undefined)
    formData.append('appearanceSizeScore', data.appearanceSizeScore.toString())
  if (data.appearanceSizeRemark) formData.append('appearanceSizeRemark', data.appearanceSizeRemark)
  if (data.structureCheck) formData.append('structureCheck', data.structureCheck)
  if (data.remarks) formData.append('remarks', data.remarks)

  await request.put<void, void>(`/molds/storage/${id}/quality-check`, formData as any)
}

// 完成入库
export async function completeStorage(id: number, cargoLocation?: string): Promise<void> {
  await request.put<void, void>(`/molds/storage/${id}/complete`, { cargoLocation })
}

// 获取入库记录详情
export async function getStorageRecordDetail(id: number): Promise<StorageRecord> {
  const data = await request.get<StorageRecord, StorageRecord>(`/molds/storage-records/${id}`)
  return data
}

// 分页查询入库记录
export async function getStorageRecordList(
  params?: StorageRecordListQuery
): Promise<PageResult<StorageRecord>> {
  const data = await request.get<StorageRecord, PageResult<StorageRecord>>(
    '/molds/storage-records',
    { params }
  )
  return data
}

// 获取模具入库历史
export async function getStorageHistory(moldId: number): Promise<StorageRecord[]> {
  const data = await request.get<StorageRecord[], StorageRecord[]>(
    `/molds/storage-histories/${moldId}`
  )
  return data
}

// 删除入库记录
export async function deleteStorageRecord(id: number): Promise<void> {
  await request.delete<void>(`/molds/storage-records/${id}`)
}

// ========== 配件管理 ==========

// 创建配件
export async function createMoldPart(data: CreateMoldPartRequest): Promise<number> {
  const result = await request.post<number, number>('/mold-parts', data)
  return result
}

// 更新配件
export async function updateMoldPart(id: number, data: UpdateMoldPartRequest): Promise<void> {
  await request.put<void, void>(`/mold-parts/${id}`, data)
}

// 删除配件
export async function deleteMoldPart(id: number): Promise<void> {
  await request.delete<void>(`/mold-parts/${id}`)
}

// 获取配件详情
export async function getMoldPartDetail(id: number): Promise<MoldPart> {
  const data = await request.get<MoldPart, MoldPart>(`/mold-parts/${id}`)
  return data
}

// 分页查询配件列表
export async function getMoldPartList(params?: MoldPartListQuery): Promise<PageResult<MoldPart>> {
  const data = await request.get<MoldPart, PageResult<MoldPart>>('/mold-parts', { params })
  return data
}

// 根据模具匹配配件（返回分组结果）
export interface MoldPartMatchResult {
  parts: Record<string, MoldPart[]>
}

export async function getMatchedParts(moldId: number): Promise<MoldPart[]> {
  const data = await request.get<MoldPartMatchResult, MoldPartMatchResult>(
    `/mold-parts/matched/${moldId}`
  )
  // 将分组数据扁平化为一个数组
  if (data?.parts) {
    return Object.values(data.parts).flat()
  }
  return []
}

// 获取已领用的配件
// 后端映射为 /api/mold-parts/borrowed/{moldId}，前端必须保持一致；
// 原 /picked-up/{moldId} 不存在，会导致 500。
export async function getBorrowedParts(moldId: number): Promise<MoldPart[]> {
  const data = await request.get<MoldPart[], MoldPart[]>(`/mold-parts/borrowed/${moldId}`)
  return data
}

// 领用配件
export async function borrowParts(data: BorrowPartsRequest): Promise<void> {
  await request.post<void, void>('/mold-parts/pickup', data)
}

// 归还配件
export async function returnParts(partIds: number[]): Promise<void> {
  await request.post<void, void>('/mold-parts/return', partIds)
}

// ========== 领用记录管理 ==========

// 提交领用申请
export async function applyBorrow(data: BorrowApplyRequest): Promise<number> {
  const result = await request.post<number, number>('/pickup-records', data)
  return result
}

// 组合申请：模具+配件（原子性）
export interface BorrowWithPartsRequest {
  moldId: number
  partIds?: number[]
  purpose: string
  remark?: string
}

export async function applyBorrowWithParts(data: BorrowWithPartsRequest): Promise<number> {
  const result = await request.post<number, number>('/pickup-records/apply-with-parts', data)
  return result
}

// 审批通过
export async function approveBorrow(
  id: number,
  data?: (ApprovalRequest & { frontPhotoFile?: File; backPhotoFile?: File }) | FormData
): Promise<void> {
  if (data instanceof FormData) {
    await request.put<void, void>(`/pickup-records/${id}/approve`, data)
  } else if (data) {
    const formData = new FormData()
    if (data.remark) formData.append('remark', data.remark)
    if (data.frontPhotoFile) formData.append('frontPhotoFile', data.frontPhotoFile)
    if (data.backPhotoFile) formData.append('backPhotoFile', data.backPhotoFile)
    await request.put<void, void>(`/pickup-records/${id}/approve`, formData)
  } else {
    await request.put<void, void>(`/pickup-records/${id}/approve`, new FormData())
  }
}

// 审批拒绝
export async function rejectBorrow(id: number, data?: ApprovalRequest): Promise<void> {
  await request.put<void, void>(`/pickup-records/${id}/reject`, data || {})
}

// 归还模具
export async function returnBorrow(id: number, data?: ReturnRequest): Promise<void> {
  await request.put<void, void>(`/pickup-records/${id}/return`, data || {})
}

// 获取待审批列表
export async function getPendingBorrowList(): Promise<BorrowRecord[]> {
  const data = await request.get<BorrowRecord[], BorrowRecord[]>('/pickup-records/pending')
  return data
}

// 分页查询领用记录
export async function getBorrowRecordList(params?: {
  page?: number
  pageSize?: number
  status?: string
  applicantId?: number
  keyword?: string
  moldId?: number
}): Promise<PageResult<BorrowRecord>> {
  const data = await request.get<BorrowRecord, PageResult<BorrowRecord>>('/pickup-records', {
    params,
  })
  return data
}

// 根据模具ID获取领用中的记录
export async function getBorrowRecordByMoldId(moldId: number): Promise<BorrowRecord | null> {
  try {
    const data = await request.get<BorrowRecord, PageResult<BorrowRecord>>('/pickup-records', {
      params: { moldId, status: 'PICKING_UP', pageSize: 1 },
    })
    return data.records?.[0] || null
  } catch {
    return null
  }
}

// 获取领用记录详情
export async function getBorrowRecordDetail(id: number): Promise<BorrowRecord> {
  const data = await request.get<BorrowRecord, BorrowRecord>(`/pickup-records/${id}`)
  return data
}

// 获取借用统计数据
export async function getBorrowStatistics(): Promise<{
  pending: number
  borrowing: number
  returned: number
  rejected: number
}> {
  const data = await request.get<
    {
      pending: number
      borrowing: number
      returned: number
      rejected: number
    },
    {
      pending: number
      borrowing: number
      returned: number
      rejected: number
    }
  >('/pickup-records/statistics')
  return data
}

// ========== 归还申请管理（基于领用记录） ==========

// 提交归还申请
export async function submitReturnApplication(
  recordId: number,
  data: ReturnRequest & { frontPhotoFile?: File; backPhotoFile?: File }
): Promise<number> {
  // 后端接收multipart/form-data，需要用FormData
  const formData = new FormData()
  if (data.returnType) formData.append('returnType', data.returnType)
  if (data.roleType) formData.append('roleType', data.roleType)
  if (data.suggestion) formData.append('suggestion', data.suggestion)
  if (data.partIds && data.partIds.length > 0) formData.append('partIds', data.partIds.join(','))
  if (data.frontPhotoFile) formData.append('frontPhotoFile', data.frontPhotoFile)
  if (data.backPhotoFile) formData.append('backPhotoFile', data.backPhotoFile)

  const result = await request.post<number, number>(
    `/pickup-records/${recordId}/return-application`,
    formData
  )
  return result
}

// 获取待确认的归还申请列表（模管员）
export async function getReturnPendingPage(params?: {
  page?: number
  pageSize?: number
}): Promise<PageResult<BorrowRecord>> {
  const data = await request.get<BorrowRecord, PageResult<BorrowRecord>>(
    '/pickup-records/return-applications/pending',
    { params }
  )
  return data
}

// 获取归还申请列表（支持按申请人过滤）
export async function getReturnApplicationsPage(params?: {
  page?: number
  pageSize?: number
  applicantId?: number
  /** [2026-09-21 新增] 还模申请日期区间，格式 yyyy-MM-dd（含首尾两天） */
  startDate?: string
  endDate?: string
  /** [2026-09-21 新增] 只看成型车间（还模人角色=SHAPER，系统按部门自动判定） */
  onlyForming?: boolean
}): Promise<PageResult<BorrowRecord>> {
  const data = await request.get<BorrowRecord, PageResult<BorrowRecord>>(
    '/pickup-records/return-applications',
    { params }
  )
  return data
}

// 获取我的归还申请列表
export async function getMyReturnApplicationsPage(params?: {
  page?: number
  pageSize?: number
}): Promise<PageResult<BorrowRecord>> {
  const data = await request.get<BorrowRecord, PageResult<BorrowRecord>>(
    '/pickup-records/return-applications/my',
    { params }
  )
  return data
}

// 成型车间最近还模记录（成型主任/生产部长工作台卡片，2026-09-21 新增）
// 后端只返回还模人角色=SHAPER（成型组，系统按部门自动判定）的申请，按申请时间倒序
export interface FormingRecentReturn {
  id: number
  moldId: number
  moldCode: string
  returnerName: string
  returnType: string
  returnTypeText: string
  status: string
  createTime: string
}

// 成型车间最近还模记录：口径为最近 3 天（今天+昨天+前天，2026-09-21 用户拍板），
// 天数窗口由后端定，前端不传条数
export async function getFormingRecentReturns(): Promise<FormingRecentReturn[]> {
  const data = await request.get<FormingRecentReturn[], FormingRecentReturn[]>(
    '/pickup-records/return-applications/forming-recent'
  )
  return data || []
}

// 模管员确认归还申请
export async function confirmReturnApplication(
  recordId: number,
  data: ReturnConfirmRequest
): Promise<void> {
  await request.put<void, void>(`/pickup-records/${recordId}/return-application/confirm`, data)
}

// 取消归还申请
export async function cancelReturnApplication(recordId: number): Promise<void> {
  await request.put<void, void>(`/pickup-records/${recordId}/return-application/cancel`)
}

// 获取归还申请详情
export async function getReturnApplicationDetail(recordId: number): Promise<BorrowRecord> {
  const data = await request.get<BorrowRecord, BorrowRecord>(
    `/pickup-records/${recordId}/return-application`
  )
  return data
}

// ========== 模具交接 API ==========

// 提交交接申请
export async function submitMoldTransfer(data: MoldTransferRequest): Promise<number> {
  const response = await request.post<{ data: number }, { data: number }>('/mold-transfers', data)
  return response.data
}

// 获取我收到的交接记录
export async function getMyReceivedTransfers(): Promise<MoldTransfer[]> {
  const data = await request.get<MoldTransfer[], MoldTransfer[]>('/mold-transfers/my/received')
  return data
}

// 分页获取我收到的交接记录
export async function getMyReceivedTransfersPage(params?: {
  page?: number
  pageSize?: number
}): Promise<PageResult<MoldTransfer>> {
  const data = await request.get<MoldTransfer, PageResult<MoldTransfer>>(
    '/mold-transfers/my/received/page',
    { params }
  )
  return data
}

// 获取待确认的交接记录（交接人视角）
export async function getPendingConfirmTransfers(): Promise<MoldTransfer[]> {
  const data = await request.get<MoldTransfer[], MoldTransfer[]>('/mold-transfers/pending/confirm')
  return data
}

// 分页获取待确认的交接记录
export async function getPendingConfirmTransfersPage(params?: {
  page?: number
  pageSize?: number
}): Promise<PageResult<MoldTransfer>> {
  const data = await request.get<MoldTransfer, PageResult<MoldTransfer>>(
    '/mold-transfers/pending/confirm/page',
    { params }
  )
  return data
}

// 获取交接记录详情
export async function getMoldTransferDetail(id: number): Promise<MoldTransfer> {
  const data = await request.get<MoldTransfer, MoldTransfer>(`/mold-transfers/${id}`)
  return data
}

// 确认交接
export async function confirmMoldTransfer(id: number): Promise<void> {
  await request.put<void, void>(`/mold-transfers/${id}/confirm`)
}

// 取消交接
export async function cancelMoldTransfer(id: number): Promise<void> {
  await request.put<void, void>(`/mold-transfers/${id}/cancel`)
}

// 搜索用户（用于@提及和选择交接人/接收人）
export async function searchUsers(keyword: string): Promise<UserSearchResult[]> {
  const data = await request.get<UserSearchResult[]>('/users/search', { params: { keyword } })
  return data || []
}

// 用户搜索结果类型
export interface UserSearchResult {
  id: number
  username: string
  realName: string
  departmentName?: string
}

// 导出模具列表
export async function exportMolds(params?: { keyword?: string; status?: string }): Promise<void> {
  return request.download('/molds/export', { params })
}

// 导入模具列表
export async function importMolds(
  file: File
): Promise<{ success: boolean; data: { success: number; fail: number }; message?: string }> {
  const formData = new FormData()
  formData.append('file', file)
  return request.post('/molds/import', formData)
}

// 下载模具导入模板
export async function downloadImportTemplate(withExamples: boolean = false): Promise<void> {
  return request.download('/molds/import-template', { params: { withExamples } })
}

// 导出配件列表
export async function exportParts(params?: { keyword?: string; status?: string }): Promise<void> {
  return request.download('/mold-parts/export', { params })
}

// 导出归还申请列表
export async function exportReturnApplications(params?: {
  page?: number
  pageSize?: number
  applicantId?: number
  keyword?: string
  status?: string
}): Promise<void> {
  return request.download('/pickup-records/return-applications/export', { params })
}

// 导出入库记录列表
export async function exportStorageRecords(params?: {
  keyword?: string
  status?: string
  storageType?: string
  moldId?: number
}): Promise<void> {
  return request.download('/molds/storage-records/export', { params })
}

// 导出领用记录列表
export async function exportBorrowRecords(params?: {
  page?: number
  pageSize?: number
  status?: string
  applicantId?: number
  keyword?: string
  moldId?: number
}): Promise<void> {
  return request.download('/pickup-records/export', { params })
}

// ==================== 模具状态变更日志 ====================

export interface MoldStateChangeLog {
  id: number
  moldId: number
  moldCode: string
  oldStatus: string
  oldStatusDesc: string
  newStatus: string
  newStatusDesc: string
  changeReason: string
  triggerSource: string
  triggerSourceDesc: string
  relatedRecordId: number | null
  operatorId: number
  operatorName: string
  createTime: string
  remark: string
}

// 获取模具状态变更历史（分页）
export async function getMoldStateChangeLogs(
  moldId: number,
  page = 1,
  pageSize = 20
): Promise<{ records: MoldStateChangeLog[]; total: number }> {
  const data = await request.get<
    MoldStateChangeLog,
    { records: MoldStateChangeLog[]; total: number; current: number; size: number }
  >(`/mold-state-change-logs/mold/${moldId}`, { params: { page, pageSize } })
  return { records: data.records || [], total: data.total || 0 }
}

// 模具状态变更日志（流转记录，用于模具详情页"流转记录"标签，按流程聚合展示）
export interface StateChangeLogItem {
  id: number
  triggerSource: string
  triggerSourceText: string
  triggerSourceIcon: string
  oldStatus: string
  newStatus: string
  changeReason: string
  relatedRecordId: number | null
  operatorId: number
  operatorName: string
  createTime: string
  remark: string | null
}

// 获取模具状态变更日志（流转记录）
export async function getStateChangeLogs(moldId: number): Promise<StateChangeLogItem[]> {
  const data = await request.get<StateChangeLogItem, StateChangeLogItem[]>(
    `/molds/${moldId}/state-change-logs`
  )
  return Array.isArray(data) ? data : []
}

// ==================== 领用/归还记录 ====================

export interface BorrowRecordItem {
  id: number
  moldId: number
  moldCode: string
  applicantId: number
  applicantName: string
  purpose: string
  status: string
  approvalLevel: number
  currentApproverId: number | null
  firstApproverId: number | null
  firstApproveTime: string | null
  secondApproverId: number | null
  secondApproveTime: string | null
  rejecterId: number | null
  rejectReason: string | null
  borrowTime: string | null
  returnTime: string | null
  createTime: string
}

// 获取模具的领用记录列表
export async function getBorrowRecordsByMold(
  moldId: number,
  page = 1,
  pageSize = 50
): Promise<{ records: BorrowRecordItem[]; total: number }> {
  const data = await request.get<
    BorrowRecordItem,
    { records: BorrowRecordItem[]; total: number; current: number; size: number }
  >('/pickup-records', { params: { page, pageSize, moldId } })
  return { records: data.records || [], total: data.total || 0 }
}

// 获取模具的归还申请记录列表
export async function getReturnApplicationsByMold(
  moldId: number,
  page = 1,
  pageSize = 50
): Promise<{ records: ReturnApplicationItem[]; total: number }> {
  const data = await request.get<
    ReturnApplicationItem,
    { records: ReturnApplicationItem[]; total: number; current: number; size: number }
  >('/return-applications', { params: { page, pageSize, moldId } })
  return { records: data.records || [], total: data.total || 0 }
}

// ==================== 模具评论 ====================

export interface MoldComment {
  id: number
  moldId: number
  moldCode: string
  userId: number
  userName: string
  content: string
  mentionedUserIds: number[]
  createdAt: string
  canDelete?: boolean
}

// 获取模具评论列表
export async function getMoldComments(
  moldId: number,
  _page = 1,
  _pageSize = 20
): Promise<{ records: MoldComment[]; total: number }> {
  // 后端直接返回数组，需要包装成分页格式
  const data = await request.get<MoldComment[], MoldComment[]>(`/molds/${moldId}/comments`)
  const comments = Array.isArray(data) ? data : []
  return { records: comments, total: comments.length }
}

// 发表评论
export async function createMoldComment(
  moldId: number,
  content: string,
  mentionedUserIds?: number[]
): Promise<void> {
  await request.post<void, CommentRequest>(`/molds/${moldId}/comments`, {
    content,
    mentionedUserIds,
  })
}

// 删除评论
export async function deleteMoldComment(moldId: number, commentId: number): Promise<void> {
  await request.delete<void>(`/molds/${moldId}/comments/${commentId}`)
}

// ==================== 模具再处理 ====================

export interface MoldReprocess {
  id: number
  moldId: number
  moldCode: string
  productSpec: string
  processType: string
  processTypeDesc: string
  status: string
  statusDesc: string
  taskId: number | null
  remark: string
  createTime: string
}

export interface MoldReprocessQuery {
  keyword?: string
  processType?: string
  status?: string
  page?: number
  pageSize?: number
}

// 分页查询再处理列表
export async function getReprocessList(
  params?: MoldReprocessQuery
): Promise<{ records: MoldReprocess[]; total: number; current: number; pages: number }> {
  const data = await request.get<
    MoldReprocess,
    { records: MoldReprocess[]; total: number; current: number; pages: number; size: number }
  >('/mold-reprocess/list', { params })
  return {
    records: data.records || [],
    total: data.total || 0,
    current: data.current || 1,
    pages: data.pages || 0,
  }
}

// 创建再处理记录
export async function createReprocess(data: {
  moldId: number
  processType: string
  remark?: string
}): Promise<number> {
  return request.post<number, number>('/mold-reprocess', data)
}

// 生成再处理任务
export async function generateReprocessTask(
  id: number
): Promise<{ taskId: number | null; message: string }> {
  return request.post<
    { taskId: number | null; message: string },
    { taskId: number | null; message: string }
  >(`/mold-reprocess/${id}/generate-task`)
}

// 删除再处理记录
export async function deleteReprocess(id: number): Promise<void> {
  await request.delete<void>(`/mold-reprocess/${id}`)
}

// ==================== 模具照片历史 ====================

export interface MoldPhotoHistory {
  id: number
  frontPhotoUrl: string // 正面照片URL
  backPhotoUrl: string // 反面照片URL
  operatorName: string
  createTime: string
  photoEvent?: string // 照片事件/上传原因
  eventDescription?: string // 事件描述
}

export function getMoldPhotoHistory(id: number) {
  return request<MoldPhotoHistory[]>({
    url: `/molds/${id}/photo-histories`,
    method: 'get',
  })
}

// ==================== 变形检查相关 ====================

export interface DeformationCheckRequest {
  moldId: number
  moldCode: string
  productSpec: string
  slotWidth: number
  bulkDensity: number
  deformationYPlus: number | null
  deformationYMinus: number | null
  deformationXPlus: number | null
  deformationXMinus: number | null
  checkTime: string
  checkUserId: number
  checkUserName: string
  remark?: string
}

export interface DeformationCheck {
  id: number
  moldId: number
  moldCode: string
  productSpec: string
  slotWidth: number
  bulkDensity: number
  deformationYPlus: number | null
  deformationYMinus: number | null
  deformationXPlus: number | null
  deformationXMinus: number | null
  checkTime: string
  checkUserId: number
  checkUserName: string
  remark: string
}

export interface PageParams {
  page?: number
  pageSize?: number
  keyword?: string
}

// 创建变形检查记录
export async function createDeformationCheck(data: DeformationCheckRequest): Promise<void> {
  await request.post<void, void>('/mold-deformation-checks', data)
}

// 分页查询变形检查记录
export async function getDeformationChecks(
  params: PageParams
): Promise<PageResult<DeformationCheck>> {
  return request.get<PageResult<DeformationCheck>>('/mold-deformation-checks/page', { params })
}

// ==================== 槽宽检测相关 ====================

export interface SlotCheckRequest {
  moldCode: string
  productSpec: string
  slotWidth: number
  bulkDensity?: number
  deformationYPlus?: number
  deformationYMinus?: number
  deformationXPlus?: number
  deformationXMinus?: number
  checkTime: string
  remark?: string
}

export interface SlotCheck {
  id: number
  moldCode: string
  productSpec: string
  slotWidth: number
  bulkDensity?: number
  deformationYPlus?: number
  deformationYMinus?: number
  deformationXPlus?: number
  deformationXMinus?: number
  checkTime: string
  checkUserName?: string
  remark?: string
}

// 创建槽宽检测记录
export async function createSlotCheck(data: SlotCheckRequest): Promise<void> {
  await request.post<void, void>('/mold-slot-width-checks', data)
}

// 分页查询槽宽检测记录
export async function getSlotChecks(params: PageParams): Promise<PageResult<SlotCheck>> {
  return request.get<SlotCheck, PageResult<SlotCheck>>('/mold-slot-width-checks', { params })
}

// 删除槽宽检测记录
export async function deleteSlotCheck(id: number): Promise<void> {
  await request.delete<void, void>(`/mold-slot-width-checks/${id}`)
}

// ========== 配件领用审批 ==========

export interface PartBorrowApplyRequest {
  partIds: number[]
  purpose: string
}

export interface PartBorrowRecord {
  id: number
  partId: number
  partCode: string
  partName: string
  applicantId: number
  applicantName: string
  departmentId?: number
  departmentName?: string
  purpose: string
  status: string
  statusText?: string
  approverId?: number
  approverName?: string
  approveTime?: string
  approveRemark?: string
  rejectReason?: string
  rejectTime?: string
  rejecterName?: string
  borrowTime?: string
  returnTime?: string
  returnRemark?: string
  returnOperatorName?: string
  createTime: string
}

// 提交配件领用申请
export async function applyPartBorrow(data: PartBorrowApplyRequest): Promise<number> {
  const result = await request.post<{ recordId: number }, { recordId: number }>(
    '/part-pickup/apply',
    data
  )
  return result.recordId
}

// 审批通过配件领用
export async function approvePartBorrow(
  id: number,
  data?: { remark?: string; frontPhoto?: File; backPhoto?: File }
): Promise<void> {
  const formData = new FormData()
  if (data?.remark) formData.append('remark', data.remark)
  if (data?.frontPhoto) formData.append('frontPhoto', data.frontPhoto)
  if (data?.backPhoto) formData.append('backPhoto', data.backPhoto)

  await request.post<void, void>(`/part-pickup/${id}/approve`, formData)
}

// 审批拒绝配件领用
export async function rejectPartBorrow(id: number, data?: { remark?: string }): Promise<void> {
  await request.post<void, void>(`/part-pickup/${id}/reject`, data || {})
}

// 归还配件
export async function returnPartBorrow(
  id: number,
  remark?: string,
  frontPhoto?: File,
  backPhoto?: File
): Promise<void> {
  const formData = new FormData()
  if (remark) formData.append('remark', remark)
  if (frontPhoto) formData.append('frontPhoto', frontPhoto)
  if (backPhoto) formData.append('backPhoto', backPhoto)

  await request.post<void, void>(`/part-pickup/${id}/return`, formData)
}

// 获取待审批配件列表
export async function getPendingPartBorrows(): Promise<PartBorrowRecord[]> {
  return request.get<PartBorrowRecord[], PartBorrowRecord[]>('/part-pickup/pending')
}

// 分页查询配件领用记录
export async function getPartBorrowRecords(params?: {
  page?: number
  pageSize?: number
  status?: string
  applicantId?: number
  keyword?: string
}): Promise<PageResult<PartBorrowRecord>> {
  return request.get<PageResult<PartBorrowRecord>>('/part-pickup/page', { params })
}

// 获取配件领用详情
export async function getPartBorrowDetail(id: number): Promise<PartBorrowRecord> {
  return request.get<PartBorrowRecord, PartBorrowRecord>(`/part-pickup/${id}`)
}

// 我的配件领用申请
export async function getMyPartBorrows(params?: {
  page?: number
  pageSize?: number
}): Promise<PageResult<PartBorrowRecord>> {
  return request.get<PageResult<PartBorrowRecord>>('/part-pickup/my/applications', { params })
}

// ==================== 成型生产记录 ====================

export interface FormingRecord {
  id?: number
  moldId?: number
  moldCode?: string
  productSpec?: string
  moldCategory?: string
  recordDate?: string
  formingHeight?: number
  formingCount?: number
  meters?: number
  bulkDensity?: number
  dryBilletWallThickness?: number
  endFace?: number
  passRate?: number
  actualMesh?: number
  outPartSeq?: string
  outPartSize?: string
  inPartSeq?: string
  inPartSize?: string
  dryBilletSize?: string
  pressMachineNo?: string
  pressure?: number
  mudHardness?: string
  dryShrinkage?: number
  abnormalRecord?: string
  workerId?: number
  workerName?: string
  status?: string
  approverId?: number
  approverName?: string
  approveTime?: string
  createTime?: string
  updateTime?: string
}

export interface FormingRecordQuery {
  page?: number
  pageSize?: number
  moldId?: number
  moldCode?: string
  status?: string
}

export interface FormingDensityHistory {
  recordDate?: string
  bulkDensity?: number
  moldCode?: string
}

// 分页查询成型记录
export async function getFormingRecordList(
  params?: FormingRecordQuery
): Promise<PageResult<FormingRecord>> {
  return request.get<PageResult<FormingRecord>>('/mold-forming-records', { params })
}

// 获取成型记录详情
export async function getFormingRecordDetail(id: number): Promise<FormingRecord> {
  return request.get<FormingRecord, FormingRecord>(`/mold-forming-records/${id}`)
}

// 新增成型记录（成型工录入）
export async function createFormingRecord(data: FormingRecord): Promise<number> {
  return request.post<number, number>('/mold-forming-records', data)
}

// 修改成型记录（仅草稿态）
export async function updateFormingRecord(id: number, data: FormingRecord): Promise<void> {
  await request.put<void, void>(`/mold-forming-records/${id}`, data)
}

// 删除成型记录
export async function deleteFormingRecord(id: number): Promise<void> {
  await request.delete<void>(`/mold-forming-records/${id}`)
}

// 审核成型记录（成型主任）
export async function approveFormingRecord(id: number): Promise<void> {
  await request.post<void, void>(`/mold-forming-records/${id}/approve`, {})
}

// 获取按当前用户领用过滤的进出泥圈下拉选项
export async function getMatchedPartsForUser(moldId: number): Promise<MoldPartMatchResult> {
  return request.get<MoldPartMatchResult, MoldPartMatchResult>(
    `/mold-forming-records/matched-parts/${moldId}`
  )
}

// 获取容重历史（容重监控数据源）
export async function getFormingDensityHistory(moldId: number): Promise<FormingDensityHistory[]> {
  return request.get<FormingDensityHistory, FormingDensityHistory[]>(
    '/mold-forming-records/density-history',
    { params: { moldId } }
  )
}

// 单副模具成型动态统计（与小程序端共用后端接口）
// 平均/最新合格率来自成型记录真实 pass_rate 字段；总米数为后端全量聚合（与米数排行同源）
export interface MoldFormingStats {
  moldCode?: string
  totalRecords?: number
  avgPassRate?: number
  latestPassRate?: number
  totalMeters?: number
  totalFormingCount?: number
  abnormalCount?: number
  lastRecordDate?: string
  daysSinceLast?: number
  latestWorker?: string
  hasData?: boolean
}

// 获取单副模具成型动态统计
export async function getFormingStats(moldCode: string): Promise<MoldFormingStats> {
  return request.get<MoldFormingStats, MoldFormingStats>('/mold-forming-records/mold-stats', {
    params: { moldCode }
  })
}

